import { Card, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import type { Player, Round } from "../types";

interface PairingsPanelProps {
  round: Round;
  players: Player[];
  onReport: (matchId: string, g1: number, g2: number) => void;
  bestOf: number;
  isActive: boolean;
}

/** Displays a round's match pairings with score reporting controls. */
export default function PairingsPanel({
  round,
  players,
  onReport,
  bestOf,
  isActive,
}: PairingsPanelProps) {
  const playerMap = new Map(players.map((p) => [p.id, p]));
  const winThreshold = Math.ceil(bestOf / 2);

  const reportedCount = round.matches.filter(
    (m) =>
      m.isBye ||
      (m.player1Games !== null &&
        m.player2Games !== null &&
        (m.player1Games >= winThreshold || m.player2Games >= winThreshold)),
  ).length;

  return (
    <Card border={isActive ? "primary" : undefined}>
      <Card.Header
        className={`d-flex justify-content-between align-items-center ${isActive ? "bg-primary text-white" : ""}`}
      >
        <span className="fw-semibold">Round {round.roundNum}</span>
        <span
          className={`small ${isActive ? "" : "text-muted"}`}
          style={{ fontFamily: "var(--font-mono-custom)" }}
        >
          {reportedCount}/{round.matches.length}
        </span>
      </Card.Header>
      <Card.Body className="p-2">
        {round.matches.map((match, idx) => {
          const p1 = playerMap.get(match.player1);
          const p2 =
            match.player2 !== null ? playerMap.get(match.player2) : null;
          const p1Won =
            match.player1Games !== null &&
            match.player1Games >= winThreshold;
          const p2Won =
            match.player2Games !== null &&
            match.player2Games >= winThreshold;

          const scoreButtons = (side: "p1" | "p2", current: number | null) =>
            Array.from({ length: winThreshold + 1 }, (_, n) => (
              <ToggleButton
                key={n}
                id={`${match.id}-${side}-${n}`}
                value={n}
                variant="outline-primary"
                size="sm"
                active={n === current}
              >
                {n}
              </ToggleButton>
            ));

          return (
            <div
              key={match.id}
              className={`px-2 py-2 ${idx > 0 ? "border-top" : ""}`}
            >
              <div className="d-flex">
                <span className="station-tag">{idx + 1}</span>
                <div className="flex-grow-1 min-w-0">
                  <div className="pairing-row">
                    <span
                      className={`text-truncate ${p1Won ? "fw-bold" : ""} ${p2Won ? "text-muted" : ""}`}
                    >
                      {p1?.name ?? "???"}
                    </span>
                    {!match.isBye && (
                      <ToggleButtonGroup
                        type="radio"
                        name={`${match.id}-p1`}
                        value={match.player1Games ?? -1}
                        onChange={(v: number) =>
                          onReport(match.id, v, match.player2Games ?? 0)
                        }
                      >
                        {scoreButtons("p1", match.player1Games)}
                      </ToggleButtonGroup>
                    )}
                  </div>
                  {match.isBye ? (
                    <div className="small text-muted mt-1 d-flex align-items-center gap-2">
                      <span className="stamp stamp--bye">Bye</span>
                      <span>auto-win</span>
                    </div>
                  ) : (
                    <div className="pairing-row mt-2">
                      <span
                        className={`text-truncate ${p2Won ? "fw-bold" : ""} ${p1Won ? "text-muted" : ""}`}
                      >
                        {p2?.name ?? "???"}
                      </span>
                      <ToggleButtonGroup
                        type="radio"
                        name={`${match.id}-p2`}
                        value={match.player2Games ?? -1}
                        onChange={(v: number) =>
                          onReport(match.id, match.player1Games ?? 0, v)
                        }
                      >
                        {scoreButtons("p2", match.player2Games)}
                      </ToggleButtonGroup>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </Card.Body>
    </Card>
  );
}
